/**
 * Confete comemorativo, sem dependência externa.
 *
 * Desenha num canvas sobreposto (pointer-events: none) que é removido sozinho
 * quando a animação acaba. `fireConfetti` devolve uma função de cancelamento
 * para o componente chamar no unmount.
 *
 * O modal usa z-[10050]; ficamos logo acima para o confete cair na frente dele.
 */

const CANVAS_Z_INDEX = 10060;
const DEFAULT_DURATION = 2200;
const PARTICLE_COUNT = 90;
const GRAVITY = 0.12;
const DRAG = 0.995;

const COLORS = [
  '#1f93ff', // woot
  '#44ce4b',
  '#ffc532',
  '#ff382d',
  '#8b5cf6',
  '#ec4899',
];

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

// Dois jatos partindo das bordas inferiores, apontando para dentro e para cima.
function createParticles(width, height) {
  const particles = [];

  for (let i = 0; i < PARTICLE_COUNT; i += 1) {
    const fromLeft = i % 2 === 0;
    const angle = fromLeft ? randomBetween(-1.2, -0.5) : randomBetween(-2.6, -1.9);
    const speed = randomBetween(9, 17);

    particles.push({
      x: fromLeft ? width * 0.15 : width * 0.85,
      y: height * 0.85,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: randomBetween(5, 11),
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rotation: randomBetween(0, Math.PI * 2),
      spin: randomBetween(-0.2, 0.2),
      stretch: randomBetween(0.4, 1),
    });
  }

  return particles;
}

export function fireConfetti({ duration = DEFAULT_DURATION } = {}) {
  if (typeof document === 'undefined' || prefersReducedMotion()) {
    return () => {};
  }

  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText = [
    'position:fixed',
    'inset:0',
    'width:100%',
    'height:100%',
    'pointer-events:none',
    `z-index:${CANVAS_Z_INDEX}`,
  ].join(';');

  const context = canvas.getContext('2d');
  if (!context) return () => {};

  const ratio = window.devicePixelRatio || 1;
  const resize = () => {
    canvas.width = window.innerWidth * ratio;
    canvas.height = window.innerHeight * ratio;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
  };
  resize();

  document.body.appendChild(canvas);
  window.addEventListener('resize', resize);

  const particles = createParticles(window.innerWidth, window.innerHeight);
  const startedAt = performance.now();
  let frameId = null;
  let stopped = false;

  const cleanup = () => {
    if (stopped) return;
    stopped = true;
    if (frameId !== null) cancelAnimationFrame(frameId);
    window.removeEventListener('resize', resize);
    canvas.remove();
  };

  const draw = now => {
    const elapsed = now - startedAt;
    const progress = Math.min(elapsed / duration, 1);

    context.clearRect(0, 0, window.innerWidth, window.innerHeight);

    particles.forEach(p => {
      p.vx *= DRAG;
      p.vy = p.vy * DRAG + GRAVITY;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.spin;

      context.save();
      context.translate(p.x, p.y);
      context.rotate(p.rotation);
      context.globalAlpha = 1 - progress;
      context.fillStyle = p.color;
      // Retângulo achatado gira e parece uma fitinha de papel.
      context.fillRect(
        -p.size / 2,
        -p.size / 2,
        p.size * p.stretch,
        p.size * 0.4
      );
      context.restore();
    });

    if (progress >= 1) {
      cleanup();
      return;
    }
    frameId = requestAnimationFrame(draw);
  };

  frameId = requestAnimationFrame(draw);

  return cleanup;
}

export default fireConfetti;
